function arc(r, opts = {}) {
    const { isLargeArc = false, counterClockwise = false } = opts;
    return ` A ${r} ${r} 0 ${Number(isLargeArc)} ${Number(!counterClockwise)}`;
}
function pos(r, angle) {
    const x = r * Math.sin(angle);
    const y = -r * Math.cos(angle);
    return ` ${x} ${y}`;
}
export class RingGraphBuilder {
    #outerRadius;
    #innerRadius;
    #maxBorderRadius;
    constructor(opts) {
        this.#outerRadius = opts.outerRadius;
        this.#innerRadius = opts.innerRadius;
        this.#maxBorderRadius = opts.maxBorderRadius;
    }
    /**
     * @param span - the angle span of the sector. MUST be in (0, 2π]
     * @returns the corner radius buildSectorPath uses for a sector of this span
     */
    getBorderRadius(span) {
        const { sin, PI } = Math;
        const R_OUT = this.#outerRadius;
        const R_IN = this.#innerRadius;
        let borderRadius = this.#maxBorderRadius;
        // can't exceed half the stroke width
        const strokeWidth = R_OUT - R_IN;
        if (borderRadius > strokeWidth / 2) {
            borderRadius = strokeWidth / 2;
        }
        // for small angles, border radius must fit within the arc
        // solving equation:
        //   angle - 2 * asin(borderRadius / R) = 0
        // where R = R₀ ± borderRadius
        if (span < PI / 2) {
            const largestOut = R_OUT / (1 / sin(span / 2) + 1);
            const largestIn = R_IN / (1 / sin(span / 2) - 1);
            const largest = Math.min(largestOut, largestIn);
            if (largest > 0 && largest < borderRadius) {
                borderRadius = largest;
            }
        }
        return borderRadius;
    }
    /**
     * @param span - the angle span of the sector. MUST be in (0, 2π]
     * @param offset - the angle offset of the sector
     * @returns the SVG path string of the sector
     */
    buildSectorPath(span, offset = 0) {
        const { cos, asin, PI } = Math;
        const R_OUT = this.#outerRadius;
        const R_IN = this.#innerRadius;
        const borderRadius = this.getBorderRadius(span);
        const [outParams, inParams] = [
            [R_OUT, -1],
            [R_IN, +1],
        ].map(([r0, borderRadiusScaler]) => {
            const r = r0 + borderRadiusScaler * borderRadius;
            const angleOffset = asin(borderRadius / r);
            return { angleOffset, borderRadius, r };
        });
        const [startAngle, endAngle] = [offset, offset + span];
        // Draw this border, path, and fill it.
        // The border has border-out, border-in, border-start, border-end.
        // The path begins at the tangent point on border-start near border-out,
        // clock wise direction.
        let path;
        // begin: border-start near out
        path = 'M';
        path += pos(outParams.r, startAngle);
        // arc to: border-out near start
        path += arc(outParams.borderRadius);
        path += pos(R_OUT, startAngle + outParams.angleOffset);
        // arc to: border-out near end
        path += arc(R_OUT, {
            isLargeArc: span - outParams.angleOffset * 2 > PI,
        });
        path += pos(R_OUT, endAngle - outParams.angleOffset);
        // arc to: border-end near out
        path += arc(outParams.borderRadius);
        path += pos(outParams.r * cos(outParams.angleOffset), endAngle);
        // line to: border-end near in
        path += ' L';
        path += pos(inParams.r * cos(inParams.angleOffset), endAngle);
        // arc to: border-in near end
        path += arc(inParams.borderRadius);
        path += pos(R_IN, endAngle - inParams.angleOffset);
        // arc to: border-in near start
        path += arc(R_IN, {
            isLargeArc: span - inParams.angleOffset * 2 > PI,
            counterClockwise: true,
        });
        path += pos(R_IN, startAngle + inParams.angleOffset);
        // arc to: border-start near in
        path += arc(inParams.borderRadius);
        path += pos(inParams.r, startAngle);
        // close
        path += ' Z';
        return path;
    }
    /**
     * @returns Props containing the shape. You may want to specify fill.
     */
    buildSectorPathSvgProps(span, offset = 0) {
        const R = this.#outerRadius;
        return {
            d: this.buildSectorPath(span, offset),
            transform: [{ translateX: R }, { translateY: R }],
            strokeWidth: 0,
        };
    }
    /**
     * @returns Props containing the shape. You may want to specify stroke.
     */
    buildCircleSvgProps() {
        return {
            cx: this.#outerRadius,
            cy: this.#outerRadius,
            r: (this.#outerRadius + this.#innerRadius) / 2,
            strokeWidth: this.#outerRadius - this.#innerRadius,
            fill: 'transparent',
        };
    }
}
